'use client'

import { useState } from 'react'
import { X } from 'lucide-react'
import type { Project, Sprint, TaskPriority } from '@/lib/types'
import { VALID_POINTS, weekStartDate, weekOfSprint } from '@/lib/dev-sprint'
import { useToast } from '@/context/ToastContext'
import type { BoardData } from './types'

const inputCls = 'w-full bg-white border border-[#E7E5DF] rounded-lg px-2.5 py-2 text-[13px] text-[#1A1916] focus:outline-none focus:ring-2 focus:ring-[#D4A843]/40'
const labelCls = 'block text-[11px] font-semibold uppercase tracking-wide text-[#AEADA8] mb-1.5'

function Shell({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-[440px] bg-white rounded-2xl shadow-2xl animate-slide-up">
        <div className="border-b border-[#EDEBE5] px-5 py-3.5 flex items-center justify-between">
          <h2 className="text-[15px] font-bold text-[#1A1916]">{title}</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-[#AEADA8] hover:text-[#1A1916] hover:bg-[#F2F1EC]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
        <div className="border-t border-[#EDEBE5] px-5 py-3 flex justify-end gap-2">{footer}</div>
      </div>
    </div>
  )
}

const addDays = (iso: string, n: number) => new Date(new Date(iso + 'T00:00:00').getTime() + n * 86400000).toISOString().slice(0, 10)

export function NewSprintModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { toast } = useToast()
  const today = new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10)
  const [name, setName] = useState('')
  const [goal, setGoal] = useState('')
  const [start, setStart] = useState(today)
  const [end, setEnd] = useState(addDays(today, 27))
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!name.trim()) { toast('Nama sprint wajib diisi', 'error'); return }
    setSaving(true)
    try {
      const res = await fetch('/api/sprints', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), goal: goal.trim(), startDate: start, endDate: end }),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal membuat sprint')
      toast('Sprint dibuat', 'success')
      onDone()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal', 'error')
    } finally { setSaving(false) }
  }

  return (
    <Shell
      title="Sprint Baru"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-2 rounded-xl text-[13px] font-medium text-[#6B6A65] hover:bg-[#F2F1EC]">Batal</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#1A1916] hover:bg-black disabled:opacity-60">{saving ? 'Menyimpan…' : 'Buat'}</button>
        </>
      }
    >
      <div><label className={labelCls}>Nama</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sprint 1 — Juli" className={inputCls} /></div>
      <div><label className={labelCls}>Goal</label><input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Fokus sprint ini…" className={inputCls} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Mulai</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>Selesai</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} /></div>
      </div>
    </Shell>
  )
}

export function NewTaskModal({
  projects, team, sprint, prefill, onClose, onDone,
}: {
  projects: Project[]
  team: BoardData['team']
  sprint: Sprint | null
  prefill: { devUsername?: string | null; week?: number; projectId?: string; dueDate?: string }
  onClose: () => void
  onDone: () => void
}) {
  const { toast } = useToast()
  const [projectId, setProjectId] = useState(prefill.projectId ?? projects[0]?.id ?? '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [assignee, setAssignee] = useState(prefill.devUsername ?? '')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [points, setPoints] = useState<number | null>(null)
  // Default deadline: prefill.dueDate (dari kalender) > minggu prefill > minggu berjalan sprint.
  const [dueDate, setDueDate] = useState(() => {
    if (prefill.dueDate) return prefill.dueDate
    if (!sprint) return ''
    const today = new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10)
    return weekStartDate(sprint, prefill.week ?? (weekOfSprint(today, sprint) ?? 1))
  })
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!projectId) { toast('Pilih project', 'error'); return }
    if (!title.trim()) { toast('Judul task wajib diisi', 'error'); return }
    setSaving(true)
    try {
      const res = await fetch('/api/dev/tasks', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, title: title.trim(), description, priority, assignee: assignee || null, dueDate: dueDate || null, points, sprintId: sprint?.id ?? null }),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal membuat task')
      toast('Task dibuat', 'success')
      onDone()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal', 'error')
    } finally { setSaving(false) }
  }

  return (
    <Shell
      title="Task Baru"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-2 rounded-xl text-[13px] font-medium text-[#6B6A65] hover:bg-[#F2F1EC]">Batal</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#1A1916] hover:bg-black disabled:opacity-60">{saving ? 'Menyimpan…' : 'Buat'}</button>
        </>
      }
    >
      {sprint && (
        <p className="text-[11px] text-[#8B8A85] -mt-1">
          Menambah ke sprint <b className="text-[#4A4844]">{sprint.name}</b>.{!dueDate && ' Tanpa deadline → masuk Backlog (belum terjadwal).'}
        </p>
      )}
      <div>
        <label className={labelCls}>Project</label>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={inputCls}>
          {projects.length === 0 && <option value="">Tidak ada project tim</option>}
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div><label className={labelCls}>Judul</label><input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Assignee</label>
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className={inputCls}>
            <option value="">— belum —</option>
            {team.filter((m) => m.username).map((m) => <option key={m.email} value={m.username as string}>{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Deadline</label>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Prioritas</label>
          <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)} className={inputCls}>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Points</label>
          <div className="flex gap-1">
            {VALID_POINTS.map((p) => (
              <button key={p} onClick={() => setPoints(points === p ? null : p)} className={`w-8 h-8 rounded-lg text-[12px] font-bold border ${points === p ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6A65] border-[#E7E5DF]'}`}>{p}</button>
            ))}
          </div>
        </div>
      </div>
      <div><label className={labelCls}>Deskripsi</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${inputCls} resize-y`} /></div>
    </Shell>
  )
}
